import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { sectionVariant, staggerContainer, itemVariant, viewportOnce, heroVariant, glowVariant } from "@/lib/motion";

const resourceCategories = [
  {
    slug: "insights",
    title: "Insights",
    description: "Strategic perspectives on growth, performance, and marketing systems. Thought leadership grounded in practice, not theory.",
    count: 12,
    intentHint: "For founders and growth leaders seeking strategic clarity.",
  },
  {
    slug: "guides",
    title: "Guides & Playbooks",
    description: "Comprehensive frameworks for implementation. Step-by-step guidance built from real engagements.",
    count: 8,
    intentHint: "For teams ready to execute with proven methodologies.",
  },
  {
    slug: "case-studies",
    title: "Case Studies",
    description: "Documented outcomes from our partnerships. Context, strategy, execution, and measurable results.",
    count: 6,
    intentHint: "For decision-makers evaluating approaches and outcomes.",
  },
  {
    slug: "frameworks",
    title: "Frameworks & Models",
    description: "Proprietary thinking tools we use to diagnose, plan, and optimize growth systems.",
    count: 5,
    intentHint: "For strategists seeking structured approaches.",
  },
  {
    slug: "tools",
    title: "Tools & Calculators",
    description: "Practical resources for measurement, planning, and diagnostics. Built from real engagement needs.",
    count: 15,
    intentHint: "For practitioners needing actionable utilities.",
  },
  {
    slug: "faqs",
    title: "FAQs & Knowledge Base",
    description: "Clear answers to common questions about services, methodologies, and working with us.",
    count: 40,
    intentHint: "For those seeking quick, specific answers.",
  },
];

const featuredResources = [
  {
    category: "Framework",
    title: "The Growth Systems Framework",
    description: "How we think about interconnected acquisition, conversion, retention, and scale.",
    slug: "/resources/frameworks/growth-systems-framework",
    stage: "Consideration",
  },
  {
    category: "Guide",
    title: "SEO Playbook for B2B",
    description: "A comprehensive approach to organic growth for complex sales cycles.",
    slug: "/resources/guides/seo-playbook-b2b",
    stage: "Decision",
  },
  {
    category: "Insight",
    title: "Why Most Growth Strategies Fail",
    description: "The structural problems behind underperforming marketing investments.",
    slug: "/resources/insights/why-growth-strategies-fail",
    stage: "Awareness",
  },
];

const intentFilters = [
  { label: "All", value: "all" },
  { label: "For Founders", value: "founders" },
  { label: "For Growth Teams", value: "growth" },
  { label: "For Enterprise", value: "enterprise" },
];

const ResourcesHub = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        title="Resources | Strategic Growth Insights & Frameworks | XperfoMax"
        description="Frameworks, perspectives, and tools from the work we do every day. No fluff. No generic advice. Substance that moves decisions forward."
      />
      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-10 md:pt-36 md:pb-16 lg:pt-44 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <motion.div 
            className="absolute top-0 right-1/4 w-[800px] h-[800px] rounded-full"
            initial="hidden"
            animate="visible"
            variants={glowVariant}
            style={{
              background: 'radial-gradient(circle, hsl(var(--primary) / 0.04) 0%, transparent 70%)',
            }}
          />
        </div>

        <div className="container-wide relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span 
                variants={heroVariant}
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block"
              >
                Resources
              </motion.span>
              <motion.h1 
                variants={heroVariant}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]"
              >
                A knowledge hub, not a blog.
              </motion.h1>
              <motion.p 
                variants={heroVariant}
                className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8"
              >
                Frameworks, perspectives, and tools from the work we do every day. 
                No fluff. No generic advice. Substance that moves decisions forward.
              </motion.p>
              
              {/* Purpose Statement */}
              <motion.div 
                variants={heroVariant}
                className="p-5 border-l-2 border-primary/30 bg-muted/20"
              >
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Every resource here is designed to educate buyers across awareness, consideration, 
                  and decision stages. We share what we know because informed partners make better decisions.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CATEGORY INTRO */}
      <section className="py-16 md:py-20 lg:py-24 border-t border-border">
        <div className="container-wide">
          <motion.div 
            className="max-w-3xl mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
              Explore by Category
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Find what moves your business forward.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Each category serves a different purpose. Insights for strategic clarity. 
              Guides for implementation. Frameworks for structured thinking. Choose based on 
              where you are in your growth journey.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {resourceCategories.map((category) => (
              <motion.div
                key={category.slug}
                variants={itemVariant}
              >
                <Link
                  to={category.slug === "tools" ? "/tools" : category.slug === "faqs" ? "/resources/faqs" : `/resources/${category.slug}`}
                  className="group block p-8 border border-border hover:border-primary/30 transition-colors h-full"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-medium text-foreground group-hover:text-primary transition-colors">
                      {category.title}
                    </h3>
                    <ArrowRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {category.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">
                      {category.count} resources
                    </span>
                  </div>
                  {/* Intent Hint */}
                  <p className="text-xs text-muted-foreground/70 mt-4 italic">
                    {category.intentHint}
                  </p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* WHY THIS MATTERS - Context Section */}
      <section className="py-16 md:py-20 lg:py-24 border-t border-border bg-muted/20">
        <div className="container-wide">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-6">
              Why we share this knowledge.
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Most companies treat content as a lead generation tactic. We treat it as a trust-building 
              exercise. The companies that work with us are better informed before we ever speak. 
              That makes every conversation more productive.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mt-12 text-left">
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">Avoid common mistakes</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Learn what typically fails before investing. Our insights highlight patterns 
                  from working with dozens of growth-stage companies.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">Build internal capability</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Our guides and frameworks can be implemented by your team. We would rather you 
                  succeed independently than create dependency.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">Make informed decisions</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Whether you work with us or not, understanding these concepts will help you 
                  evaluate any growth approach more critically.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="py-16 md:py-20 lg:py-24 border-t border-border">
        <div className="container-wide">
          <motion.div 
            className="max-w-3xl mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
              Start Here
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Essential reading.
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              These three resources represent the foundation of our approach. Start here to 
              understand how we think about growth systems.
            </p>
          </motion.div>

          <motion.div 
            className="space-y-0"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {featuredResources.map((resource, index) => (
              <motion.div
                key={resource.slug}
                variants={itemVariant}
              >
                <Link
                  to={resource.slug}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-border hover:bg-muted/30 transition-colors -mx-4 px-4 md:-mx-8 md:px-8"
                >
                  <div className="md:col-span-2">
                    <span className="text-xs uppercase tracking-wider text-primary font-medium">
                      {resource.category}
                    </span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                  </div>
                  <div className="md:col-span-4">
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {resource.description}
                    </p>
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between md:justify-end gap-4">
                    <span className="text-xs text-muted-foreground/70">{resource.stage}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </Link>
              </motion.div>
            ))}
            <div className="border-t border-border" />
          </motion.div>
        </div>
      </section>

      {/* DECISION SUPPORT CTA */}
      <section className="py-16 md:py-20 lg:py-24 border-t border-border bg-muted/30">
        <div className="container-wide">
          <motion.div 
            className="max-w-2xl mx-auto text-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={sectionVariant}
          >
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">
              Not sure where to start?
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              If you are evaluating growth approaches and want a structured conversation about 
              what might work for your specific situation, we are happy to help clarify options.
            </p>
            <Link 
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              Start a conversation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesHub;
